import { useState } from "react";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "../../data/content";
import { CtaButton, scrollToId } from "./Primitives";
import { ThemeToggle } from "../theme/ThemeToggle";
import { useLetsTalk } from "./LetsTalkModal";

// Route-aware nav click: hash links scroll on landing, otherwise route home + scroll.
function useNavClick() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  return (to) => {
    if (to.startsWith("/")) {
      navigate(to);
      return;
    }
    if (to.startsWith("#")) {
      if (pathname === "/") {
        scrollToId(to);
      } else {
        navigate(`/${to}`);
      }
    }
  };
}

export function Nav() {
  const { scrollY } = useScroll();
  const { pathname } = useLocation();
  const handleNav = useNavClick();
  const { open: openLetsTalk } = useLetsTalk();
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useMotionValueEvent(scrollY, "change", (y) => {
    const prev = scrollY.getPrevious() ?? 0;
    setScrolled(y > 24);
    setHidden(y > prev && y > 320 && !open);
  });

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: hidden ? -110 : 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="fixed inset-x-0 top-0 z-50"
        data-testid="site-nav"
      >
        <div className="mx-auto mt-3 flex w-full max-w-[1440px] items-center justify-between px-4 md:mt-4 md:px-8">
          <div
            className={`flex w-full items-center justify-between rounded-full border px-4 py-2.5 transition-all duration-500 md:px-5 ${
              scrolled ? "glass border-line shadow-[0_10px_40px_-20px_rgba(0,0,0,0.4)]" : "border-transparent"
            }`}
          >
            <button
              onClick={() => handleNav("#top")}
              data-testid="nav-logo"
              className="flex shrink-0 items-center"
              aria-label="RunLoop home"
            >
              <img src="/runloop_wordmark.png" alt="RunLoop" className="h-7 w-auto object-contain object-left brightness-0 dark:brightness-100 md:h-8" />
            </button>

            <nav className="hidden items-center gap-1 lg:flex">
              {NAV_LINKS.map((l) => {
                const isRoute = l.to.startsWith("/");
                const active = isRoute && pathname === l.to;
                return (
                  <button
                    key={l.label}
                    data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => handleNav(l.to)}
                    className={`group relative rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                      l.pill
                        ? "bg-brand/90 text-ink hover:bg-brand"
                        : `${active ? "text-content" : "text-content/80"} hover:text-content`
                    }`}
                  >
                    {l.label}
                    {!l.pill && (
                      <span
                        className={`absolute bottom-1.5 left-4 right-4 h-[2px] origin-center rounded-full bg-brand transition-transform duration-300 ease-out ${
                          active ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                        }`}
                      />
                    )}
                  </button>
                );
              })}
            </nav>

            <div className="flex items-center gap-2 md:gap-3">
              <ThemeToggle />
              <div className="hidden sm:block">
                <CtaButton to="" testid="nav-cta" className="!px-5 !py-2.5" onClick={() => openLetsTalk("menu")}>
                  Let&apos;s Talk
                </CtaButton>
              </div>
              <button
                className="grid h-10 w-10 place-items-center rounded-full border border-line text-content lg:hidden"
                onClick={() => setOpen(true)}
                aria-label="Open menu"
                data-testid="nav-menu-open"
              >
                <Menu size={18} />
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] bg-page"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu"
          >
            <div className="flex h-full flex-col px-6 py-6">
              <div className="flex items-center justify-between">
                <img src="/runloop_wordmark.png" alt="RunLoop" className="h-8 w-auto object-contain object-left brightness-0 dark:brightness-100" />
                <button
                  className="grid h-11 w-11 place-items-center rounded-full border border-line text-content"
                  onClick={() => setOpen(false)}
                  aria-label="Close menu"
                  data-testid="nav-menu-close"
                >
                  <X size={20} />
                </button>
              </div>
              <nav className="mt-10 flex flex-1 flex-col justify-center gap-1">
                {NAV_LINKS.map((l, i) => (
                  <motion.button
                    key={l.label}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.05 }}
                    onClick={() => {
                      setOpen(false);
                      setTimeout(() => handleNav(l.to), 60);
                    }}
                    className="py-3 text-left font-display text-4xl font-semibold tracking-tight text-content"
                    data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {l.label}
                  </motion.button>
                ))}
              </nav>
              <CtaButton
                to=""
                testid="mobile-cta"
                className="w-full justify-center"
                onClick={() => {
                  setOpen(false);
                  setTimeout(() => openLetsTalk("menu"), 60);
                }}
              >
                Let&apos;s Talk
              </CtaButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
